import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import LoadingScreen from 'src/components/loading-screen';
import { LOCAL_STORAGE_KEYS } from 'src/constants/localStorage';
import { clearAllAssistantChatCaches } from 'src/sections/@dashboard/visit/utils/assistantChatCache';

// Kept outside the reset list: this records whose preferences the list holds.
export const ORGANIZATION_STORAGE_CONTEXT_KEY = 'organization.storageContext';

interface OrganizationStorageIdentity {
  accountId: string;
  userId: string;
  organizationId: string | null;
  role: string | null;
}

export const organizationStorageIdentity = (
  identity: OrganizationStorageIdentity,
): string => JSON.stringify(identity);

const StorageIdentityContext = createContext<string | null>(null);

// The context this tab prepared storage for. Held in memory, not read from
// localStorage, which another tab may already have switched to its context.
export const useStorageIdentity = (): string | null =>
  useContext(StorageIdentityContext);

interface OrganizationStorageBoundaryProps {
  identity: string | null;
  pending: boolean;
  children: ReactNode;
}

export function OrganizationStorageBoundary({
  identity,
  pending,
  children,
}: OrganizationStorageBoundaryProps): JSX.Element {
  const [preparedIdentity, setPreparedIdentity] = useState<string | null>(null);

  useEffect(() => {
    if (pending || identity === null) return;

    if (localStorage.getItem(ORGANIZATION_STORAGE_CONTEXT_KEY) !== identity) {
      Object.values(LOCAL_STORAGE_KEYS).forEach((key) =>
        localStorage.removeItem(key),
      );
      clearAllAssistantChatCaches();
      localStorage.setItem(ORGANIZATION_STORAGE_CONTEXT_KEY, identity);
    }
    setPreparedIdentity(identity);
  }, [identity, pending]);

  // Filter components read storage only in their state initializers. Mounting
  // them before clearing a changed context would preserve another context's
  // filters in memory even after its storage entries were removed.
  if (pending || (identity !== null && preparedIdentity !== identity)) {
    return <LoadingScreen />;
  }

  return (
    <StorageIdentityContext.Provider value={identity}>
      {children}
    </StorageIdentityContext.Provider>
  );
}
